import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { UseHandleBusiness } from "0.Mine/1.Hooks/business/business";
import { Business } from "0.Mine/3.Interfaces/business";

import { useSession } from "./sessionContext";

export interface RegisteredBusiness {
  businesses: Business[];
  selectedBusiness: Business | null;
}

interface BusinessContextType {
  session: RegisteredBusiness | null;
  loading: boolean;
  addBusiness: (business: Business, callAPI: boolean) => void;
  selectBusiness: (businessId: string) => void;
  updateBusiness: (updatedBusiness: Partial<Business>, callAPI: boolean) => void;
  clearSession: () => void;
  changeBusinessIds: (businessId: number, newId: number, newUserId: number) => void;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [session, setSession] = useState<RegisteredBusiness | null>(null);
  const { session: UserSession } = useSession();
  const [loading, setLoading] = useState<boolean>(true);
  const { getAllBusinesses, addBusiness: handleAddBusiness, getOneBusiness } = UseHandleBusiness();
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const FETCH_COOLDOWN = 5000; // 5 seconds cooldown between fetches

  useEffect(() => {
    if (!UserSession) {
      setSession(null);
      navigate("/signin", { replace: true });
      return;
    }

    const fetchBusinesses = async () => {
      try {
        setLoading(true);

        // Always fetch fresh data first
        const freshBusinesses = await getAllBusinesses();

        if (!freshBusinesses || !Array.isArray(freshBusinesses)) {
          console.error("Invalid businesses data received");
          return;
        }

        const selectedBusinessId = localStorage.getItem("selectedBusiness");
        let selectedBusiness = null;

        if (selectedBusinessId) {
          // Find the business in our fresh data
          selectedBusiness =
            freshBusinesses.find((b) => String(b.id) === selectedBusinessId) || null;
        }

        // If we had a selected business but it's not in the fresh data, or no business was selected,
        // select the first available business
        if ((!selectedBusiness || !selectedBusinessId) && freshBusinesses.length > 0) {
          selectedBusiness = freshBusinesses[0];
          localStorage.setItem("selectedBusiness", String(selectedBusiness.id));
        }

        const newSession: RegisteredBusiness = {
          businesses: freshBusinesses,
          selectedBusiness,
        };

        // Update local storage
        localStorage.setItem("business_session", JSON.stringify(newSession));
        localStorage.setItem("business_session_validated", "true");

        setSession(newSession);
      } catch (error) {
        console.error("Failed to load businesses:", error);
        // Clear invalid cache on error
        localStorage.removeItem("business_session");
        localStorage.removeItem("business_session_validated");
      } finally {
        setLoading(false);
      }
    };

    // Always fetch on initial load or when validation is needed
    const needsValidation =
      !session || localStorage.getItem("business_session_validated") !== "true";
    if (needsValidation) {
      fetchBusinesses();
    }
  }, [UserSession, navigate, getAllBusinesses]); // Keep dependencies minimal

  // Function to add a new business
  const addBusiness = async (business: Business, callAPI: boolean) => {
    setSession((prevSession) => {
      const updatedBusinesses = prevSession ? [...prevSession.businesses, business] : [business];

      const newSession: RegisteredBusiness = {
        businesses: updatedBusinesses,
        selectedBusiness: prevSession?.selectedBusiness || null,
      };
      localStorage.setItem("business_session", JSON.stringify(newSession));
      return newSession;
    });

    if (callAPI) {
      const response = await handleAddBusiness(business);
      if (response) {
        console.log("CHanging id to ", response);
        changeBusinessIds(business.id, response.id, response.userId);
      }
    }
  };

  // Function to select a business
  const selectBusiness = (businessId: string) => {
    setSession((prevSession) => {
      if (!prevSession) return null;

      // Convert both IDs to numbers for comparison
      const numericBusinessId = Number(businessId);
      const currentSelectedId = prevSession.selectedBusiness?.id;

      // If the business is already selected, don't update
      if (currentSelectedId === numericBusinessId) {
        return prevSession;
      }

      const selectedBusiness =
        prevSession.businesses.find((b) => Number(b.id) === numericBusinessId) || null;

      // If we couldn't find the business, don't update
      if (!selectedBusiness) {
        console.warn(`Business with ID ${businessId} not found`);
        return prevSession;
      }

      // Clear cached data when changing business
      localStorage.removeItem("customFields");
      localStorage.removeItem("business_session_validated");

      const newSession: RegisteredBusiness = {
        ...prevSession,
        selectedBusiness,
      };

      // Update both localStorage items
      localStorage.setItem("selectedBusiness", businessId);
      localStorage.setItem("business_session", JSON.stringify(newSession));

      return newSession;
    });

    // Force a fresh data fetch after business selection
    const fetchBusinesses = async () => {
      try {
        setLoading(true);
        const freshBusinesses = await getAllBusinesses();

        if (!freshBusinesses || !Array.isArray(freshBusinesses)) {
          console.error("Invalid businesses data received");
          return;
        }

        setSession((prevSession) => {
          if (!prevSession) return null;

          const selectedBusiness = freshBusinesses.find((b) => String(b.id) === businessId) || null;

          const newSession: RegisteredBusiness = {
            businesses: freshBusinesses,
            selectedBusiness: selectedBusiness || prevSession.selectedBusiness,
          };

          localStorage.setItem("business_session", JSON.stringify(newSession));
          localStorage.setItem("business_session_validated", "true");

          return newSession;
        });
      } catch (error) {
        console.error("Failed to refresh businesses:", error);
      } finally {
        setLoading(false);
      }
    };

    // Fetch fresh data after a short delay to ensure state updates have completed
    setTimeout(fetchBusinesses, 100);
  };

  // New function to update the selected business
  const updateBusiness = (updatedBusiness: Partial<Business>, callAPI: boolean) => {
    setSession((prevSession) => {
      if (!prevSession || !prevSession.selectedBusiness) return prevSession;

      const updatedBusinesses = prevSession.businesses.map((b) =>
        b.id === prevSession.selectedBusiness!.id ? { ...b, ...updatedBusiness } : b
      );

      const newSession: RegisteredBusiness = {
        businesses: updatedBusinesses,
        selectedBusiness: { ...prevSession.selectedBusiness, ...updatedBusiness },
      };

      localStorage.setItem("business_session", JSON.stringify(newSession));
      return newSession;
    });
  };

  const changeBusinessIds = (businessId: number, newId: number, newUserId: number) => {
    setSession((prevSession) => {
      if (!prevSession) return prevSession;

      // Update the business in the businesses array
      const updatedBusinesses = prevSession.businesses.map((b) =>
        b.id === businessId ? { ...b, id: newId, userId: newUserId } : b
      );

      // Also update the selectedBusiness if it matches
      const updatedSelectedBusiness =
        prevSession.selectedBusiness && prevSession.selectedBusiness.id === businessId
          ? { ...prevSession.selectedBusiness, id: newId, userId: newUserId }
          : prevSession.selectedBusiness;

      const newSession: RegisteredBusiness = {
        businesses: updatedBusinesses,
        selectedBusiness: updatedSelectedBusiness,
      };

      localStorage.setItem("business_session", JSON.stringify(newSession));
      return newSession;
    });
  };

  // Function to clear session
  const clearSession = () => {
    setSession(null);
    localStorage.removeItem("business_session");
  };

  const value = useMemo(
    () => ({
      session,
      loading,
      addBusiness,
      selectBusiness,
      updateBusiness,
      clearSession,
      changeBusinessIds,
    }),
    [session, loading]
  );

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
};

// Custom Hook for Using Business Context
export const useBusinessContext = (): BusinessContextType => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error("useBusinessContext must be used within a BusinessProvider");
  }
  return context;
};
